import React, { useCallback } from 'react';
import { DefaultValues, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { useDialog } from '@/components/template/Dialog';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import InlineInput from '@/components/InlineInput';
import { SelectOption } from '@/components/Select';
import CompanyDepartmentSelector from '@/components/QuerySelectors/CompanyDepartmentSelector';

import { ICompanyInviteForm, companyInviteSchema } from './schema';

interface IProps {
  defaultValues?: DefaultValues<ICompanyInviteForm>;
  onSubmit: SubmitHandler<ICompanyInviteForm>;
}

const CompanyInviteForm: React.FC<IProps> = ({ onSubmit, defaultValues }) => {
  const { handleClose } = useDialog();
  const form = useForm<ICompanyInviteForm>({
    defaultValues: {
      fullName: '',
      email: '',
      departments: [],
      ...defaultValues,
    },
    resolver: zodResolver(companyInviteSchema),
  });

  const {
    control,
    formState: { isSubmitting },
  } = form;

  const handleSubmit = useCallback<SubmitHandler<ICompanyInviteForm>>(
    (data, e) => {
      handleClose();
      return onSubmit(data, e);
    },
    [handleClose, onSubmit],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="grid gap-4 py-4">
          <FormField
            control={control}
            name="fullName"
            render={({ field }) => (
              <InlineInput title="Nome" disabled={isSubmitting} {...field} />
            )}
          />
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <InlineInput title="E-mail" disabled={isSubmitting} {...field} />
            )}
          />
          <FormField
            control={control}
            name="departments"
            render={({ field }) => (
              <FormItem className="grid grid-cols-4 items-center gap-4">
                <FormLabel className="text-right">Departamento</FormLabel>
                <div className="col-span-3">
                  <CompanyDepartmentSelector
                    multiple
                    value={field.value}
                    onChange={(value: SelectOption[]) => field.onChange(value)}
                  />
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        </div>
        <DialogFooter>
          <Button className="hidden" variant="secondary">
            Import from CSV
          </Button>

          <Button type="submit" disabled={isSubmitting} loading={isSubmitting}>
            Invite
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export default CompanyInviteForm;
